<template>
  <two-column-layout>
    <card-container>
      <h2>{{ $t(i18n.license_plate_step.title) }}</h2>
      <p>{{ $t(i18n.license_plate_step.description) }}</p>

      <license-plate-input-fragment
        :label="$t(i18n.license_plate.label)"
        :placeholder="$t(i18n.license_plate.placeholder)"
        v-model="form.vehicle.licensePlate"
        :disabled="licensePlateFieldDisabled"
        :is-invalid="isFieldInvalid($v.form.vehicle)"
        show-info
        @info-clicked="onInfoClicked('REGISTER_SINGLE_VEHICLE_INFO_LICENSE_PLATE')"
        @input="onLicensePlateInputUpdated">
        <template #result>
          <p v-if="vehicleInformation" v-html="vehicleInformation" :class="$style.vehicleResult"></p>
          <field-error-messages v-if="!form.vehicle.validationErrorCode" :validation-state="$v.form.vehicle"/>
          <div v-if="form.vehicle.validationErrorCode">
            <media-item-info-block variant="warning" icon="phone" item-tag="REGISTER_SINGLE_VEHICLE_INVALID_LICENSE_PLATE" />
          </div>
        </template>
      </license-plate-input-fragment>

      <div v-if="!form.vehicle.validationErrorCode">
        <div v-if="requiredManualReportCode">
          <fragment-separator></fragment-separator>

          <text-input-fragment
              :label="$t(i18n.input.report_code.label)"
              :placeholder="$t(i18n.input.report_code.placeholder)"
              v-model="form.manualReportCode"
              :disabled="reportCodeFieldDisabled"
              :is-invalid="isFieldInvalid($v.form.manualReportCode)"
              show-info
              @info-clicked="onInfoClicked('REGISTER_SINGLE_VEHICLE_INFO_REPORT_CODE')"
              @input="onReportCodeInputUpdated">
            <span>{{ $t(i18n.input.report_code.description) }}</span>
            <template #result>
              <field-error-messages v-if="!licensePlateFieldDisabled" :validation-state="$v.form.manualReportCode"/>
            </template>
          </text-input-fragment>
        </div>

        <div v-if="requiredManualCatalogValue">
          <fragment-separator></fragment-separator>

          <text-input-fragment
            :label="$t(i18n.input.catalog_value.label)"
            :placeholder="$t(i18n.input.catalog_value.placeholder)"
            v-model="form.manualCatalogValue"
            :is-invalid="isFieldInvalid($v.form.manualCatalogValue)"
            show-info
            @info-clicked="onInfoClicked('REGISTER_SINGLE_VEHICLE_INFO_CATALOG_VALUE')"
          >
            <span>{{ $t(i18n.input.catalog_value.description) }}</span>
            <template #result>
              <field-error-messages :validation-state="$v.form.manualCatalogValue"/>
            </template>
          </text-input-fragment>
        </div>

        <fragment-separator></fragment-separator>

        <radio-group-fragment
          :label="$t(i18n.radio_group.vehicle_utilization.label)"
          group-name="group vehicle utilization"
          :options="radioGroupUtilizationOptions"
          :is-invalid="isFieldInvalid($v.form.vehicleUtilization)"
          show-info
          v-model="form.vehicleUtilization"
          @info-clicked="onInfoClicked('REGISTER_SINGLE_VEHICLE_INFO_USAGE')"
        >
          <template #result>
            <field-error-messages :validation-state="$v.form.vehicleUtilization"/>
          </template>
        </radio-group-fragment>

        <fragment-separator></fragment-separator>

        <text-fragment v-if="form.vehicle.liabilityAlreadyCovered"
          :label="$t(i18n.radio_group.liability.label)"
          show-info
          @info-clicked="onInfoClicked('REGISTER_SINGLE_VEHICLE_INFO_WA')"
        >
          <span>{{ $t(i18n.radio_group.liability.not_applicable) }}</span>
        </text-fragment>

        <radio-group-fragment v-if="!form.vehicle.liabilityAlreadyCovered"
          :label="$t(i18n.radio_group.liability.label)"
          group-name="group liability"
          :options="radioGroupLiabilityOptions"
          :is-invalid="isFieldInvalid($v.form.liability)"
          show-info
          v-model="form.liability"
          @info-clicked="onInfoClicked('REGISTER_SINGLE_VEHICLE_INFO_WA')"
        >
          <span>{{ $t(i18n.radio_group.liability.description) }}</span>
          <template #result>
            <field-error-messages :validation-state="$v.form.liability" />
          </template>
        </radio-group-fragment>

        <radio-group-fragment
          :label="$t(i18n.radio_group.casco_insurance.label)"
          group-name="group casco insurance"
          :options="radioGroupCascoInsuranceOptions"
          :is-invalid="isFieldInvalid($v.form.cascoInsurance)"
          show-info
          v-model="form.cascoInsurance"
          @info-clicked="onInfoClicked('REGISTER_SINGLE_VEHICLE_INFO_CASCO')"
        >
          <template #result>
            <field-error-messages :validation-state="$v.form.cascoInsurance" />
          </template>
        </radio-group-fragment>
      </div>
    </card-container>

    <button-group direction="right" top-spacing="l" v-if="!form.vehicle.validationErrorCode">
        <button-fragment :disabled="isLoading" @click="nextStep()">{{$t(i18n.button.next)}}</button-fragment>
    </button-group>

    <template #right>
      <media-item-card blank item-tag="REGISTER_SINGLE_VEHICLE_INFO_RECEIPT" />
    </template>
  </two-column-layout>
</template>

<script>
  import {Utils} from "@bvm/core";
  import * as LicensePlateUtil from "@bvm/core/src/utils/license-plate/license-plate-util";
  import {createNamespacedHelpers} from "vuex";
  import {required} from "vuelidate/lib/validators";
  import {
    ButtonFragment,
    ButtonGroup,
    CardContainer,
    FragmentSeparator,
    LicensePlateInputFragment,
    RadioGroupFragment,
    TextFragment,
    TextInputFragment,
    TwoColumnLayout
  } from "@bvm/forms";
  import MediaItemModal from "../../shared/media-item-modal";
  import {getProductModel, validateLicensePlate} from "../api/single-vehicle-api";
  import {checkReportCode} from "../../../api/vehicle-api";

  const { mapState, mapMutations } = createNamespacedHelpers(
  "registerSingleVehicle"
);

const i18nBuilder = new Utils.I18n.I18nBuilder("register_single_vehicle_form")
  .add("license_plate_step", b => b.add("title").add("description"))
  .add("button", b => b.add("next"))
  .add("license_plate", b => b.add("label").add("placeholder"))
  .add("input", b => b
    .add("report_code", b => b.add("label").add("placeholder").add("description"))
    .add("catalog_value", b => b.add("label").add("placeholder").add("description"))
  )
  .add("radio_group", b => b
    .add("vehicle_utilization", bb => bb.add("label").add("leasing").add("teaching").add("renting").add("business"))
    .add("liability", bb => bb.add("label").add("description").add("not_applicable"))
    .add("casco_insurance", bb => bb.add("label").add("none").add("limited_coverage").add("full_coverage"))
    .add("generic", bb => bb.add("yes").add("no"))
  );

const i18nValidationBuilder = new Utils.I18n.I18nBuilder("validations_field")
    .add("license_plate_invalid")
    .add("report_code")
    .add("report_code_invalid")
    .add("catalog_value");

const reportCodeValidation = /^[0-9]{4}$/;
const catalogValueValidation = /^[0-9]{1,6}$/;

export const messageKeys = [...i18nBuilder.getKeys(), ...i18nValidationBuilder.getKeys()];

export default {
  name: "license-plate-step",
  components: {
    TwoColumnLayout,
    ButtonGroup,
    ButtonFragment,
    CardContainer,
    LicensePlateInputFragment,
    TextFragment,
    TextInputFragment,
    RadioGroupFragment,
    FragmentSeparator,
    "media-item-card": () => import("../components/media-item-card"),
    "field-error-messages": () => import("../../shared/field-error-messages"),
    "media-item-info-block": () => import("../components/media-item-info-block")
  },
  data() {
    return {
      i18n: i18nBuilder.getData(),
      invalidLicensePlateMediaItem: null,
      licensePlateFieldDisabled: false,
      reportCodeFieldDisabled: false,
      requiredManualReportCode: false,
      isManualReportCodeValid: true,
      requiredManualCatalogValue: false,
      isLoading: false,
      form: {
        vehicle: {
          licensePlate: null,
          type: "",
          info: "",
          liabilityAlreadyCovered: null,
          validationErrorCode: null
        },
        manualReportCode: null,
        manualCatalogValue: null,
        vehicleUtilization: null,
        liability: null,
        cascoInsurance: null
      }
    };
  },
  computed: {
    ...mapState(["vehicle", "vehicleUtilization", "coverage"]),
    radioGroupUtilizationOptions() {
      return {
        teaching: this.$t(this.i18n.radio_group.vehicle_utilization.teaching),
        business: this.$t(this.i18n.radio_group.vehicle_utilization.business)
      };
    },
    radioGroupLiabilityOptions() {
      return {
        y: this.$t(this.i18n.radio_group.generic.yes),
        n: this.$t(this.i18n.radio_group.generic.no)
      };
    },
    radioGroupCascoInsuranceOptions() {
      return {
        full: this.$t(this.i18n.radio_group.casco_insurance.full_coverage),
        limited: this.$t(this.i18n.radio_group.casco_insurance.limited_coverage),
        none: this.$t(this.i18n.radio_group.casco_insurance.none)
      };
    },
    vehicleInformation() {
       return this.form.vehicle.type ? `${this.form.vehicle.type}<br/>${this.form.vehicle.info}` : this.form.vehicle.info;
    }
  },
  created() {
    this.form.vehicle = this.vehicle;
    this.form.vehicleUtilization = this.vehicleUtilization;
    this.form.liability = this.coverage.WA;
    this.form.cascoInsurance = this.coverage.CASCO;

    this.onLicensePlateInputUpdated(this.vehicle.licensePlate);
  },
  methods: {
    ...mapMutations(["setVehicleInfo", "setManualReportCode", "setManualCatalogValue", "setVehicleUtilization", "setCoverages", "setProductModelCoverages", "resetCoverages"]),
    nextStep() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.isLoading = true;
        this.updateToState().then(() => {
          this.isLoading = false;
          this.$emit("nextStep");
        }).catch(() => {
          // Set an error code when state or handing product model fails to block flow
          this.form.vehicle.validationErrorCode = "UPDATE_STATE";
          this.isLoading = false;
        });
      }
    },
    updateToState() {
      return new Promise((resolve, reject) => {
        this.setVehicleInfo(this.form.vehicle);
        this.setManualReportCode(this.requiredManualReportCode ? this.form.manualReportCode : null);
        this.setManualCatalogValue(this.requiredManualCatalogValue ? this.form.manualCatalogValue : null);
        this.setVehicleUtilization(this.form.vehicleUtilization);
        this.setCoverages({
          WA: this.form.liability,
          CASCO: this.form.cascoInsurance
        });

       getProductModel({
         licensePlate: this.form.vehicle.licensePlate,
         vehicleUtilization: this.form.vehicleUtilization
       }).then((result) => {
         this.setProductModelCoverages(result);
         resolve();
       }).catch(() => {
         reject();
       });
      });
    },
    onInfoClicked(mediaItemTag) {
            this.$bvmModal.show(MediaItemModal, {
          itemTag: mediaItemTag
            }, {
          clickToClose: true
            });
    },
    onLicensePlateInputUpdated(licensePlateInput) {
      const trimmedLicensePlate = LicensePlateUtil.trim(licensePlateInput || "");
      const formatValid = LicensePlateUtil.isValid(trimmedLicensePlate);

      this.form.vehicle.licensePlate = formatValid
        ? LicensePlateUtil.format(trimmedLicensePlate)
        : trimmedLicensePlate;

      // Reset state
      this.form.vehicle.validationErrorCode = null;
      this.form.vehicle.type = "";
      this.form.vehicle.info = "";
      this.requiredManualReportCode = false;
      this.requiredManualCatalogValue = false;

      if (!formatValid) {
        return;
      }

      this.licensePlateFieldDisabled = true;
      validateLicensePlate(trimmedLicensePlate).then(result => {
        if (result.valid === true) {
          this.form.vehicle.type = result.data.vehicleType;
          this.form.vehicle.info = result.data.vehicleInfo;
          this.checkVehicleLiabilityAlreadyCovered(result);
          this.resetCoverages();
          this.checkVehicleContextRequired(result);
        }

        this.form.vehicle.validationErrorCode = result.errorCode;
        this.licensePlateFieldDisabled = false;
      }).catch(() => {
        this.licensePlateFieldDisabled = false;
      });
    },
    onReportCodeInputUpdated(reportCodeInput) {
      if (!reportCodeInput.match(reportCodeValidation)) {
        return;
      }

      this.reportCodeFieldDisabled = true;
      this.isManualReportCodeValid = true; // Reset
      this.$v.form.manualReportCode.$touch();
      checkReportCode(this.form.vehicle.licensePlate, reportCodeInput).then(result => {
        this.isManualReportCodeValid = result.value === true;
        this.reportCodeFieldDisabled = false;
      }).catch(() => {
        this.isManualReportCodeValid = false;
        this.reportCodeFieldDisabled = false;
      });
    },
    isFieldInvalid(field) {
      return field.$dirty && field.$invalid;
    },
    checkVehicleLiabilityAlreadyCovered(validationResult) {
      this.form.vehicle.liabilityAlreadyCovered = validationResult.data.liabilityAlreadyCovered;
      this.form.liability = this.form.vehicle.liabilityAlreadyCovered ? 'not_applicable' : null;
    },
    checkVehicleContextRequired(validationResult) {
      if (!validationResult.requireExtraContext) {
        return;
      }

      this.requiredManualReportCode = !!validationResult.context["NO_REPORT_CODE_AVAILABLE"];
      this.requiredManualCatalogValue = !!validationResult.context["NO_CATALOG_VALUE_AVAILABLE"];
    }
  },
  validations() {
    return {
      form: {
        vehicle: {
          licensePlate: {field_required: required},
          info: {license_plate_invalid: required}
        },
        manualReportCode: { report_code: (value) => !this.requiredManualReportCode || reportCodeValidation.test(value), report_code_invalid: () => this.isManualReportCodeValid },
        manualCatalogValue: { catalog_value: (value) => !this.requiredManualCatalogValue || catalogValueValidation.test(value) },
        vehicleUtilization: { option_required: required },
        liability: { option_required: required },
        cascoInsurance: { option_required: required }
      }
    };
  }
};
</script>
<style module lang="scss">
  .vehicleResult {
    margin-top: 20px;
  }
</style>